import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { qk } from '../queries/keys';
import { fetchTags } from '../queries/tags';
import { ChevronDownIcon, XIcon } from './icons';
import { TagScopeIcon, TagScopeLabel } from './TagScope';
import { cx } from './ui';

/**
 * The tag filter on the library: a chip that opens the list of every tag
 * this person may use, with a search box for when there are many. Each
 * one is a checkbox; several checked narrow to recordings that carry all
 * of them. The chosen ones sit beside the chip, each with a way off.
 */
export function TagFilter({ value, onChange }: { value: string[]; onChange: (next: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const query = useQuery({ queryKey: qk.tags(), queryFn: () => fetchTags(), staleTime: 15_000 });
  const tags = query.data?.tags ?? [];
  const shown = tags.filter((tag) => tag.name.toLowerCase().includes(text.trim().toLowerCase()));
  const chosen = value.map((id) => tags.find((tag) => tag.id === id)).filter((tag) => tag !== undefined);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Nothing to filter by is nothing to show: the chip appears with the first tag.
  if (!query.isPending && tags.length === 0 && value.length === 0) return null;

  const toggle = (id: string) => onChange(value.includes(id) ? value.filter((one) => one !== id) : [...value, id]);

  return (
    <>
      <div ref={root} className="relative inline-flex">
        <button
          type="button"
          onClick={() => setOpen((current) => !current)}
          aria-haspopup="dialog"
          aria-expanded={open}
          className={cx(
            'inline-flex h-8 items-center gap-2 rounded-full border pl-3.5 pr-3 text-caption transition-colors duration-120',
            'focus:border-signal focus:outline-none',
            value.length > 0
              ? 'border-signal/60 bg-signal/10 text-chalk'
              : 'border-hairline bg-slate-850 text-chalk-dim hover:border-slate-600 hover:text-chalk',
          )}
        >
          {value.length === 0 ? 'Any tag' : value.length === 1 ? 'Tagged' : `${value.length} tags`}
          <ChevronDownIcon size={12} className="text-chalk-faint" />
        </button>

        {open ? (
          <div
            role="dialog"
            aria-label="Filter by tag"
            className="absolute left-0 top-full z-30 mt-2 w-64 rounded-md border border-hairline bg-slate-900 p-3 shadow-raised"
          >
            {tags.length > 6 ? (
              <input
                type="text"
                value={text}
                onChange={(event) => setText(event.target.value)}
                placeholder="Find a tag"
                aria-label="Find a tag"
                autoFocus
                className="mb-2 h-8 w-full rounded-xs border border-chalk/20 bg-slate-950/70 px-2 text-caption text-chalk placeholder:text-chalk-faint focus:border-signal focus:outline-none"
              />
            ) : null}
            <ul className="max-h-64 space-y-0.5 overflow-auto">
              {shown.map((tag) => (
                <li key={tag.id}>
                  <label className="flex cursor-pointer items-center gap-2.5 rounded px-1.5 py-1.5 text-body text-chalk hover:bg-slate-800">
                    <input type="checkbox" checked={value.includes(tag.id)} onChange={() => toggle(tag.id)} className="accent-signal" />
                    <span className="truncate">{tag.name}</span>
                    <span className="ml-auto flex shrink-0 items-center gap-1.5 text-micro text-chalk-faint">
                      <TagScopeLabel scope={tag.scope} />
                      {tag.videoCount > 0 ? <span className="tnum">· {tag.videoCount}</span> : null}
                    </span>
                  </label>
                </li>
              ))}
              {shown.length === 0 ? <li className="px-1.5 py-1.5 text-caption text-chalk-faint">No tag matches that.</li> : null}
            </ul>
            {value.length > 0 ? (
              <button type="button" onClick={() => onChange([])} className="mt-2 text-caption text-chalk-dim hover:text-chalk">
                Clear
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      {chosen.map((tag) => (
        <span
          key={tag.id}
          className="inline-flex h-8 items-center gap-1 rounded-full border border-signal/60 bg-signal/10 pl-3 pr-1.5 text-caption text-chalk"
        >
          <TagScopeIcon scope={tag.scope} size={10} className="text-chalk-faint" />
          <span className="max-w-[10rem] truncate">{tag.name}</span>
          <button
            type="button"
            aria-label={`Stop filtering by ${tag.name}`}
            onClick={() => toggle(tag.id)}
            className="flex h-5 w-5 items-center justify-center rounded-full text-chalk-faint hover:bg-slate-700 hover:text-chalk"
          >
            <XIcon size={10} />
          </button>
        </span>
      ))}
    </>
  );
}
